import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, Circle, AlertTriangle, RefreshCw, ArrowRight } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { DemoBadge } from "@/components/Badges";

const STATUS_ICON = {
  completed: <CheckCircle2 className="text-emerald-400" size={20} />,
  processing: <Loader2 className="animate-spin text-cyan-400" size={20} />,
  pending: <Circle className="text-slate-600" size={20} />,
};

export default function Processing() {
  const { jobId } = useParams();
  const [params] = useSearchParams();
  const songId = params.get("song");
  const nav = useNavigate();
  const [job, setJob] = useState(null);
  const [error, setError] = useState("");
  const timer = useRef(null);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(`/jobs/${jobId}`);
      setJob(data);
      if (data.status === "completed" || data.status === "failed") {
        clearInterval(timer.current);
      }
    } catch (err) {
      setError(errMsg(err));
      clearInterval(timer.current);
    }
  }, [jobId]);

  useEffect(() => {
    poll();
    timer.current = setInterval(poll, 1500);
    return () => clearInterval(timer.current);
  }, [poll]);

  const retry = async () => {
    await api.post(`/jobs/${jobId}/retry`);
    timer.current = setInterval(poll, 1500);
  };

  const done = job?.status === "completed";
  const failed = job?.status === "failed";
  const completedStages = job?.stages?.filter((s) => s.status === "completed").length || 0;
  const totalStages = job?.stages?.length || 1;

  return (
    <div className="mx-auto max-w-xl space-y-6 sw-fade-up">
      <header className="text-center">
        <div className="mb-2 flex items-center justify-center gap-2">
          <h1 className="font-display text-2xl font-extrabold text-white">Analyzing</h1>
          <DemoBadge label="DEMO ENGINE" />
        </div>
        <p className="text-sm text-slate-400">{job?.song_title}</p>
      </header>

      {/* progress ring */}
      <div className="rounded-2xl border border-[#222A38] bg-[#131822] p-6">
        <div className="mb-4 h-2 w-full overflow-hidden rounded-full bg-[#0B0E14]">
          <div
            className="h-full rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 transition-all duration-500"
            style={{ width: `${(completedStages / totalStages) * 100}%` }}
          />
        </div>
        <div className="space-y-2" data-testid="processing-stages">
          {job?.stages?.map((s) => (
            <div key={s.key} data-testid={`stage-${s.key}`} className="flex items-center gap-3 rounded-lg bg-[#0B0E14] px-3 py-2.5">
              {STATUS_ICON[s.status]}
              <span className={`flex-1 text-sm ${s.status === "completed" ? "text-white" : s.status === "processing" ? "text-cyan-300" : "text-slate-500"}`}>
                {s.label}
              </span>
              <span className="font-mono-sw text-[10px] uppercase tracking-wider text-slate-500">{s.status}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-3 text-center text-xs text-amber-200/80">
        Results are AI-estimated and produced by a clearly-labeled DEMO engine. Real audio-analysis APIs can be connected later.
      </div>

      {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{error}</div>}

      {done && (
        <button data-testid="open-studio-btn" onClick={() => nav(`/studio/${songId || job.song_id}`)} className="flex w-full items-center justify-center gap-2 rounded-xl bg-cyan-500 px-4 py-3.5 font-semibold text-[#0B0E14] transition-colors hover:bg-cyan-400">
          Open in Studio <ArrowRight size={18} />
        </button>
      )}

      {failed && (
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            <AlertTriangle size={18} className="mt-0.5 shrink-0" />
            <div>
              Analysis failed. Your original upload is preserved.
              {job?.error && <div className="mt-1 font-mono-sw text-xs text-red-400/80">{job.error}</div>}
            </div>
          </div>
          <button data-testid="retry-job-btn" onClick={retry} className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1A202C] px-4 py-3 font-semibold text-white transition-colors hover:bg-[#222A38]">
            <RefreshCw size={16} /> Retry Analysis
          </button>
        </div>
      )}
    </div>
  );
}
